import { BoxType, isRosterCellRole, stripFrameRolesForRoster } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomGroupTemplateBox, CustomTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import { rosterFrameRoleDropped } from './boxes/group-box';
import type { NormalizeIssue, PersistedBookModelV1, PersistedCollageV1, PersistedSpreadV1 } from './types';

/**
 * Гейт ростерной ячейки на ЧТЕНИИ уже-v1 документа, который кодек пропускает по ссылке.
 *
 * Тот же закон, что исполняют decode и encode группы (`group-box.ts`), и та же единственная чистка
 * (`stripFrameRolesForRoster`) — расходиться им нельзя. Гейт закрывает ввод мимо UI: импорт PSD,
 * накат дизайн-пака, прямой PATCH сажают в ячейку человека группы, не глядя на роль ребёнка, и
 * оставленный тип напечатал бы портрет ученика под именем педагога.
 *
 * Copy-on-write: документ, которому чистка не нужна, возвращается ТОЙ ЖЕ ссылкой — на этом держится
 * экономия быстрого пути. Обход идёт по группам, а не по всем коробкам дерева.
 */
export function sweepRosterFrameRoles(
  model: PersistedBookModelV1,
  onIssue: (issue: NormalizeIssue) => void,
): PersistedBookModelV1 {
  const cover = sweepSpread(model.cover, 'cover', onIssue);
  let spreadsChanged = false;
  const spreads = model.spreads.map((spread, index) => {
    const next = sweepSpread(spread, `spreads[${index}]`, onIssue);
    if (next !== spread) spreadsChanged = true;
    return next;
  });

  if (cover === model.cover && !spreadsChanged) return model;

  return { ...model, cover: cover as PersistedBookModelV1['cover'], spreads };
}

function sweepSpread<T extends PersistedSpreadV1>(spread: T, path: string, onIssue: (issue: NormalizeIssue) => void): T {
  if (!Array.isArray(spread.collages)) return spread;

  let changed = false;
  const collages = spread.collages.map((collage, index) => {
    const next = sweepCollage(collage, `${path}.collages[${index}]`, onIssue);
    if (next !== collage) changed = true;
    return next;
  });

  return changed ? { ...spread, collages } : spread;
}

function sweepCollage(collage: PersistedCollageV1, path: string, onIssue: (issue: NormalizeIssue) => void): PersistedCollageV1 {
  // Смешанный документ доезжает сюда легаси-коллажом без `boxes` — он уже в целевой форме своего пути.
  if (!Array.isArray(collage.boxes)) return collage;

  const boxes = sweepBoxes(collage.boxes, `${path}.boxes`, onIssue);
  return boxes === collage.boxes ? collage : { ...collage, boxes };
}

function sweepBoxes(
  boxes: readonly CustomTemplateBox[],
  path: string,
  onIssue: (issue: NormalizeIssue) => void,
): readonly CustomTemplateBox[] {
  // Гейт судит только группы, а коллаж на 90 % из них не состоит: без этой проверки каждый коллаж
  // книги платил бы новым массивом за обход, который заведомо ничего не меняет.
  if (!boxes.some(box => box.type === BoxType.GROUP)) return boxes;

  let changed = false;
  const next = boxes.map((box, index) => {
    if (box.type !== BoxType.GROUP) return box;
    const swept = sweepGroup(box, `${path}[${index}]`, onIssue);
    if (swept !== box) changed = true;
    return swept;
  });

  return changed ? next : boxes;
}

function sweepGroup(
  group: CustomGroupTemplateBox,
  path: string,
  onIssue: (issue: NormalizeIssue) => void,
): CustomGroupTemplateBox {
  if (!Array.isArray(group.boxes)) return group;

  // Вложенная группа судится СВОИМ родом, как и на круге: роль ребёнка знает только его прямой родитель.
  const inner = sweepBoxes(group.boxes, `${path}.boxes`, onIssue);
  const peopleType = group.params.peopleType;

  if (isRosterCellRole(peopleType)) {
    for (const box of inner) {
      if (box.type !== BoxType.IMAGE || box.params.frameRole === undefined) continue;
      onIssue({ severity: 'warn', path, reason: rosterFrameRoleDropped(box.params.frameRole, peopleType) });
    }
  }

  const stripped = stripFrameRolesForRoster(inner as CustomTemplateBox[], peopleType);
  return stripped === group.boxes ? group : { ...group, boxes: stripped };
}
